import { Component, Input, Output, EventEmitter, OnChanges, input } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { LeaveApplyRequest } from '@interfaces/contractor-flow.model';
import { FileUploadService } from '@app/shared/services/file-upload.service';
import { VendorService } from '@services/vendor.service';
import { ToastrService } from 'ngx-toastr';
import { ContractorFlowService } from '../contractor-flow.service';
import { AuthService } from '@app/core/auth.service';
import { VendorEmployeeEnum } from '@app/shared/constants/global.enum';
import { SummaryItem } from '@app/shared/interfaces/vendor.model';

@Component({
  selector: 'app-contractor-apply-leave',
  templateUrl: './contractor-apply-leave.component.html',
  styleUrls: ['./contractor-apply-leave.component.scss'],
})
export class ContractorApplyLeaveComponent implements OnChanges {
  @Input() showModal: boolean = false;
  @Output() closeModal = new EventEmitter<void>();
  @Output() dataSaved = new EventEmitter<void>();
  @Input() quarterlyLimit: number | null = null;
  selectedDate: string = '';
  numberOfLeaves: number = 1;
  comments: string = '';
  selectedFiles: File[] = [];
  applyLeaveForm!: FormGroup;
  VendorEmployeeEnum = VendorEmployeeEnum

  uploadedLeaves: File | null = null;
  leaveType = [
    { value: 'Half Paid Leave', label: 'Half Paid Leave' },
    { value: 'Paid Leave', label: 'Paid Leave' },
    { value: 'Half Unpaid Leave', label: 'Half Unpaid Leave' },
    { value: 'Unpaid Leave', label: 'Unpaid Leave' },
  ];
  dateLabel = 'Select Date*';
  startDatePlaceholder = 'Start Date';
  endDatePlaceholder = 'End Date';
  selectedCard: string = '';
  uploadedLeavePath: string = '';
  startDate: string = '';
  endDate: string = '';
  fileError: string | null = null;
  publicHolidays: string[] = [];
  paidLeaveDates: string[] = [];
  unpaidLeaveDates: string[] = [];
  halfPaidLeaveDates: string[] = [];
  halfUnpaidLeaveDates: string[] = [];
  allMarkedDates: string[] = [];
  billableDates: string[] = [];         // yyyy-MM-dd — for date picker highlight
  rawBillableDates: string[] = [];      // dd-MM-yyyy — for billing API calls
  private currentHolidayYear: number = 0;
  timesheetData: any[] = [];
  processedLeaveSummary: SummaryItem[] = [];

  constructor(
    private fileUploadService: FileUploadService,
    private toasterService: ToastrService,
    private formBuilder: FormBuilder,
    private applyLeaveService: ContractorFlowService,
    private authService: AuthService,
    private vendorService: VendorService,
  ) {}

  ngOnChanges(): void {
    this.initializeForm();
    this.refreshLeaveData();
  }

  private initializeForm(): void {
    this.applyLeaveForm = this.formBuilder.group({
      leaveType: ['', [Validators.required]],
      numberOfLeaves: [{value: '', disabled: true}, [Validators.required]],
      comments: ['', [Validators.required]],
      startDate: ['', [Validators.required]],
      endDate: ['', [Validators.required]],
    });
  }

  onClose() {
    this.showModal = false;
    this.resetForm();
    this.closeModal.emit();
  }

  private resetForm(): void {
    this.applyLeaveForm.reset({
      leaveType: '',
      numberOfLeaves: '',
      comments: '',
      startDate: '',
      endDate: '',
    });
    this.selectedFiles = [];
    this.uploadedLeaves = null;
    this.fileError = null;
  }

onSubmit() {
  if (this.fileError) {
    this.toasterService.error(this.fileError);
    return;
  }

  if (this.applyLeaveForm.get('numberOfLeaves')?.value === 0) {
    this.toasterService.error(this.VendorEmployeeEnum.noLeaveSelected);
    return;
  }

  if (this.applyLeaveForm.valid) {
    const formValues = this.applyLeaveForm.value;
    this.validateDateRangeStatus(formValues.startDate, formValues.endDate).then(isValidStatus => {
      if (!isValidStatus) {
        return;
      }

      this.validateQuarterlyLimit(formValues.leaveType, formValues.startDate).then(result => {
        if (!result.isValid) {
          return;
        }

        const employeeId = this.authService.userProfile?.employeeId;
        const leaveData: LeaveApplyRequest = {
          employeeId: employeeId ? employeeId.toString() : '',
          comments: formValues.comments,
          attachmentUrl: this.uploadedLeavePath,
          noOfLeaves: this.numberOfLeaves,
          status: 'Submitted',
          leaveEntries: [
            {
              fromDate: formValues.startDate,
              toDate: formValues.endDate,
              leaveType: formValues.leaveType,
            },
          ],
        };

        this.applyLeaveService.saveApplyLeave(leaveData).subscribe({
          next: (res: any) => {
            if (res?.status === 'Failure') {
              this.toasterService.error(res.reason || this.VendorEmployeeEnum.errorLeave);
              return;
            }
            if (result.warning) {
              this.toasterService.warning(result.warning);
            }
            this.toasterService.success(this.VendorEmployeeEnum.successLeave);
            this.refreshLeaveData();

            // If a billing array exists, call billing API merging the submitted
            // leave dates in — regardless of whether they were previously billed.
            if (this.rawBillableDates.length > 0) {
              const empId = this.authService.userProfile?.employeeId?.toString() ?? '';

              // Convert submitted working days yyyy-MM-dd → dd-MM-yyyy for billing API
              const submittedWorkingDays = this.getWorkingDaysInRange(
                formValues.startDate,
                formValues.endDate,
              );
              const leaveDatesFormatted = submittedWorkingDays.map((d) => {
                const [y, m, day] = d.split('-');
                return `${day}-${m}-${y}`;
              });
              const merged = Array.from(
                new Set([...this.rawBillableDates, ...leaveDatesFormatted]),
              );

              this.vendorService
                .recalculateEmployeeTimesheet(empId, merged)
                .subscribe({
                  next: () => {
                    this.dataSaved.emit();
                    this.onClose();
                  },
                  error: () => {
                    // Billing update failed — leave was still saved, reload anyway
                    this.dataSaved.emit();
                    this.onClose();
                  },
                });
            } else {
              this.dataSaved.emit();
              this.onClose();
            }
          },
          error: (error) => {
            this.toasterService.error(this.VendorEmployeeEnum.errorLeave);
          },
        });
      });
    });
  } else {
    this.toasterService.error(this.VendorEmployeeEnum.validationError);
    this.applyLeaveForm.markAllAsTouched();
  }
}

  handleDateRangeChange(filteredData: any): void {
    if (filteredData && filteredData.startDate && filteredData.endDate) {
      const leaveType = this.applyLeaveForm.get('leaveType')?.value;
      const markedDatesInRange = this.getMarkedDatesInRange(filteredData.startDate, filteredData.endDate, leaveType);
      
      if (markedDatesInRange.length > 0) {
        this.toasterService.error(
          `Cannot select the following dates as they are already marked: ${markedDatesInRange.join(', ')}. Please select different dates.`
        );
        this.applyLeaveForm.patchValue({
          startDate: '',
          endDate: '',
          numberOfLeaves: 0
        });
        this.numberOfLeaves = 0;
        return;
      }
      
      const baseDays = this.calculateBusinessDays(
        filteredData.startDate,
        filteredData.endDate,
        leaveType
      );
      const numberOfLeaves = this.calculateNumberOfLeaves(baseDays);
      
      this.applyLeaveForm.patchValue({
        startDate: filteredData.startDate,
        endDate: filteredData.endDate,
        numberOfLeaves: numberOfLeaves
      });
      this.numberOfLeaves = numberOfLeaves;
    } else {
      this.applyLeaveForm.patchValue({
        numberOfLeaves: 0
      });
      this.numberOfLeaves = 0;
    }
  }

  handleLeaveType(event: any) {
    this.applyLeaveForm.patchValue({
      leaveType: event,
    });
    const formValues = this.applyLeaveForm.value;
    if (formValues.startDate && formValues.endDate) {
      const markedDatesInRange = this.getMarkedDatesInRange(formValues.startDate, formValues.endDate, event);
      if (markedDatesInRange.length > 0) {
        this.toasterService.error(
          `Cannot select the following dates as they are already marked: ${markedDatesInRange.join(', ')}. Please select different dates.`
        );
        this.applyLeaveForm.patchValue({ startDate: '', endDate: '', numberOfLeaves: 0 });
        this.numberOfLeaves = 0;
        return;
      }
      const baseDays = this.calculateBusinessDays(
        formValues.startDate,
        formValues.endDate,
        event
      );
      const numberOfLeaves = this.calculateNumberOfLeaves(baseDays, event);
      
      this.applyLeaveForm.patchValue({
        numberOfLeaves: numberOfLeaves
      });
      this.numberOfLeaves = numberOfLeaves;
    }
  }

  onLeaveSelected(file: File) {
    const allowedTypes = [
      'application/pdf',
      'image/png',
      'image/jpeg',
      'image/jpg',
    ];
    if (!allowedTypes.includes(file.type)) {
      this.fileError = this.VendorEmployeeEnum.fileError;
      this.uploadedLeaves = null;
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      this.fileError = this.VendorEmployeeEnum.fileSizeError;
      this.uploadedLeaves = null;
      return;
    }
    this.fileError = null;
    this.uploadedLeaves = file;
    this.fileUploadService.uploadFile(file, 'applyLeave').subscribe({
      next: (res) => {
        if (res?.success && res?.filePath) {
          this.applyLeaveForm.get('uploadedInvoice')?.setValue(file);
          this.uploadedLeavePath = res?.filePath;
        }
      },
      error: (err) => {
        this.toasterService.error(this.VendorEmployeeEnum.uploadLeaveAttachmentError);
      },
    });
  }
  onLeaveRemoved() {
    this.uploadedLeaves = null;
  }

  getAllPublicHolidays(data: any[]): string[] {
    return Array.from(
      new Set(
        data.flatMap((monthData: any) => monthData.publicHolidayDates ?? [])
      )
    ).sort();
  }

  private extractAllLeaveDates(data: any[]): void {
    const allPaidLeaves: string[] = [];
    const allUnpaidLeaves: string[] = [];
    const allHalfPaidLeaves: string[] = [];
    const allHalfUnpaidLeaves: string[] = [];

    data.forEach((monthData: any) => {
      if (monthData.paidLeaveDates) {
        allPaidLeaves.push(...monthData.paidLeaveDates);
      }
      if (monthData.unpaidLeaveDates) {
        allUnpaidLeaves.push(...monthData.unpaidLeaveDates);
      }
      if (monthData.halfPaidLeaveDates) {
        allHalfPaidLeaves.push(...monthData.halfPaidLeaveDates);
      }
      if (monthData.halfUnpaidLeaveDates) {
        allHalfUnpaidLeaves.push(...monthData.halfUnpaidLeaveDates);
      }
    });

    // Remove duplicates and sort
    this.paidLeaveDates = Array.from(new Set(allPaidLeaves)).sort();
    this.unpaidLeaveDates = Array.from(new Set(allUnpaidLeaves)).sort();
    this.halfPaidLeaveDates = Array.from(new Set(allHalfPaidLeaves)).sort();
    this.halfUnpaidLeaveDates = Array.from(new Set(allHalfUnpaidLeaves)).sort();
  }

  private combineAllMarkedDates(): void {
    // Combine all marked dates including public holidays and all leave types
    this.allMarkedDates = [
      ...this.publicHolidays,
      ...this.paidLeaveDates,
      ...this.unpaidLeaveDates,
      ...this.halfPaidLeaveDates,
      ...this.halfUnpaidLeaveDates
    ];
    // Remove duplicates and sort
    this.allMarkedDates = Array.from(new Set(this.allMarkedDates)).sort();
  }

  private loadPublicHolidays(location: string, year: number): void {
    if (this.currentHolidayYear === year) return; // already loaded for this year
    this.currentHolidayYear = year;
    this.vendorService.getCbreLeaves(location, year).subscribe({
      next: (dates: string[]) => {
        this.publicHolidays = dates;
        this.combineAllMarkedDates();
      },
      error: () => {}
    });
  }

  /**
   * Fetches billableDates for the given month/year from the contractor timesheet API.
   * Stores raw dd-MM-yyyy for billing API calls and normalised yyyy-MM-dd for the picker.
   */
  private loadBillableDates(month: number, year: number): void {
    const employeeId = this.authService.userProfile?.employeeId;
    if (!employeeId) return;
    this.applyLeaveService.getContractorTimesheetApi(employeeId.toString()).subscribe({
      next: (response: any) => {
        const data: any[] = response?.data ?? [];
        const monthEntry = data.find(
          (item: any) =>
            parseInt(item.monthNumber) === month && item.year === year,
        );
        const raw: string[] = monthEntry?.billableDates ?? [];
        this.rawBillableDates = raw;
        // Normalise dd-MM-yyyy → yyyy-MM-dd for the date picker
        this.billableDates = raw.map((d) => {
          const [day, m, y] = d.split('-');
          return `${y}-${m}-${day}`;
        });
      },
      error: () => {
        this.rawBillableDates = [];
        this.billableDates = [];
      },
    });
  }

  /**
   * Returns all non-weekend dates (yyyy-MM-dd) between startDate and endDate inclusive.
   */
  private getWorkingDaysInRange(startDate: string, endDate: string): string[] {
    const dates: string[] = [];
    const end = new Date(endDate);
    for (let d = new Date(startDate); d <= end; d.setDate(d.getDate() + 1)) {
      const day = d.getDay();
      if (day !== 0 && day !== 6) {
        dates.push(this.formatDateToString(d));
      }
    }
    return dates;
  }

  private refreshLeaveData(month?: number, year?: number): void {
    const employeeId = this.authService.userProfile?.employeeId;
    const location = (this.authService.userProfile && (this.authService.userProfile as any).city) ? (this.authService.userProfile as any).city : '';
    const vendorId = this.authService.userProfile?.vendorId;    
    let targetMonth: number, targetYear: number;
    if (month && year) {
      targetMonth = month;
      targetYear = year;
    } else {
      const today = new Date();
      targetMonth = today.getMonth() + 1;
      targetYear = today.getFullYear();
    }
    // Load public holidays for the year (no-op if already loaded for this year)
    this.loadPublicHolidays(location, targetYear);
    // Load billing dates for this month so the calendar can highlight them
    this.loadBillableDates(targetMonth, targetYear);
    if (!employeeId) return;
    this.vendorService.getLeaveSummary(employeeId.toString(), location, vendorId || null, targetMonth, targetYear).subscribe({
      next: (data: any) => {
        if (data) {
          this.timesheetData = Array.isArray(data) ? data : [data];
          this.processLeaveSummary();
          this.paidLeaveDates = data.paidLeaveDates || [];
          this.unpaidLeaveDates = data.unpaidLeaveDates || [];
          this.halfPaidLeaveDates = data.halfPaidLeaveDates || [];
          this.halfUnpaidLeaveDates = data.halfUnpaidLeaveDates || [];
          this.combineAllMarkedDates();
        }
      },
      error: (error: any) => {
        this.toasterService.error(this.VendorEmployeeEnum.errorLeave);
      },
    });
  }

  calculateBusinessDays(startDate: string, endDate: string, leaveType?: string): number {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);
    if (start > end) return 0;

    const isHalfLeave = leaveType === 'Half Paid Leave' || leaveType === 'Half Unpaid Leave';
    let businessDays = 0;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const day = d.getDay();
      const dateStr = this.formatDateToString(d);
      if (day !== 0 && day !== 6) {
        const isBlocked = isHalfLeave
          ? this.isDateBlockedForHalfLeave(dateStr, leaveType!)
          : this.allMarkedDates.includes(dateStr);
        if (!isBlocked) {
          businessDays++;
        }
      }
    }
    return businessDays;
  }

  private isDateBlockedForHalfLeave(dateStr: string, leaveType: string): boolean {
    if (this.publicHolidays.includes(dateStr)) return true;
    if (this.paidLeaveDates.includes(dateStr) || this.unpaidLeaveDates.includes(dateStr)) return true;
    return this.getHalfLeaveCountForDate(dateStr) >= 2;
  }

  private getHalfLeaveCountForDate(dateStr: string): number {
    const paidCount = this.halfPaidLeaveDates.filter(d => d === dateStr).length;
    const unpaidCount = this.halfUnpaidLeaveDates.filter(d => d === dateStr).length;
    return paidCount + unpaidCount;
  }

  private getMarkedDatesInRange(startDate: string, endDate: string, leaveType?: string): string[] {
    const markedDatesInRange: string[] = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    const isHalfLeave = leaveType === 'Half Paid Leave' || leaveType === 'Half Unpaid Leave';

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = this.formatDateToString(d);
      const isBlocked = isHalfLeave
        ? this.isDateBlockedForHalfLeave(dateStr, leaveType!)
        : this.allMarkedDates.includes(dateStr);
      if (isBlocked) {
        markedDatesInRange.push(dateStr);
      }
    }
    
    return markedDatesInRange;
  }

  private calculateNumberOfLeaves(baseDays: number, leaveType?: string): number {
    if (baseDays === 0) return 0;
    
    const currentLeaveType = leaveType || this.applyLeaveForm.get('leaveType')?.value;
    if(currentLeaveType ==="" || currentLeaveType === undefined){
      this.toasterService.error(this.VendorEmployeeEnum.leaveTypeSelection);
       return 0;
    }
    const isHalfLeave = currentLeaveType === 'Half Paid Leave' || currentLeaveType === 'Half Unpaid Leave';
    
    return isHalfLeave ? baseDays * 0.5 : baseDays;
  }

  private formatDateToString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getPublicHolidaysByMonth(month: string, year: number, data: any[]): string[] {
    const monthData = data.find((item: any) => 
      item.monthNumber === month && item.year === year
    );
    
    return monthData?.publicHolidayDates || [];
  }

  private validateDateRangeStatus(startDate: string, endDate: string): Promise<boolean> {
    const employeeId: any = this.authService.userProfile?.employeeId;
    return new Promise((resolve) => {
      this.applyLeaveService.getContractorTimesheetApi(employeeId.toString()).subscribe({
        next: (response: any) => {
          const data = response?.data;
          if (!Array.isArray(data) || data.length === 0) {
            this.toasterService.error(this.VendorEmployeeEnum.timesheetValidationError);
            return resolve(false);
          }

          const start = new Date(startDate);
          const end = new Date(endDate);
          const invalidMonths: string[] = [];

          for (let d = new Date(start); d <= end; d.setMonth(d.getMonth() + 1)) {
            const year = d.getFullYear();
            const month = d.getMonth() + 1;
            const monthData = data.find((item: any) =>
              parseInt(item.monthNumber) === month && item.year === year
            );
            if (monthData?.status && monthData.status !== 'Pending Submission' && monthData.status !== 'Rejected') {
              const monthName = new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
              if (!invalidMonths.includes(`${monthName} (Status: ${monthData.status})`)) {
                invalidMonths.push(`${monthName} (Status: ${monthData.status})`);
              }
            }
            d.setDate(1);
          }

          if (invalidMonths.length) {
            this.toasterService.error(
              `Cannot apply leave for the selected date range. The following months do not have "Pending Submission" or "Rejected" status: ${invalidMonths.join(', ')}. Please ensure timesheet status is "Pending Submission" or "Rejected" before applying for leave.`
            );
            resolve(false);
          } else {
            resolve(true);
          }
        },
        error: () => {
          this.toasterService.error(this.VendorEmployeeEnum.timesheetValidationError);
          resolve(false);
        }
      });
    });
  }

  private validateQuarterlyLimit(leaveType: string, startDate: string): Promise<{ isValid: boolean; warning?: string }> {
    if (leaveType !== 'Paid Leave' && leaveType !== 'Half Paid Leave') {
      return Promise.resolve({ isValid: true });
    }

    const selectedDate = new Date(startDate);
    const selectedMonth = selectedDate.getMonth() + 1;
    const selectedYear = selectedDate.getFullYear();
    const quarter = this.getQuarterFromMonth(selectedMonth);
    
    const employeeId = this.authService.userProfile?.employeeId;
    if (!employeeId) {
      this.toasterService.error('Employee ID not found');
      return Promise.resolve({ isValid: false });
    }

    const location = (this.authService.userProfile && (this.authService.userProfile as any).location)
      ? (this.authService.userProfile as any).location : '';
    const vendorId = null;

    return new Promise((resolve) => {
      this.applyLeaveService.getContractorTimesheetApi(employeeId.toString()).subscribe({
        next: (response: any) => {
          if (response?.data?.length > 0) {
            const quarterData = this.getQuarterDataFromTimesheet(response.data, selectedMonth, selectedYear);
            
            if (quarterData) {
              const quarterlyLimit = quarterData.quarterlyLimit || 3.5;
              const quarterlyLeavesAvailed = quarterData.quarterlyLeavesAvailed || 0;
              const requestedLeaves = this.numberOfLeaves;
              const totalAfterRequest = quarterlyLeavesAvailed + requestedLeaves;

              if (totalAfterRequest > quarterlyLimit) {
                const remaining = quarterlyLimit - quarterlyLeavesAvailed;
                this.toasterService.error(
                  `Quarterly limit exceeded for Q${quarter} ${selectedYear}! ` +
                  `Available: ${remaining.toFixed(1)} days, Requested: ${requestedLeaves} days, ` +
                  `Limit: ${quarterlyLimit} days per quarter. You can apply for Unpaid Leave for the remaining days if needed.`
                );
                resolve({ isValid: false });
              } else {
                const remainingAfter = quarterlyLimit - totalAfterRequest;
                const warning = remainingAfter <= 1
                  ? `After this request, you will have ${remainingAfter.toFixed(1)} days remaining in Q${quarter} ${selectedYear}.`
                  : undefined;
                resolve({ isValid: true, warning });
              }
            } else {
              resolve({ isValid: true });
            }
          } else {
            this.toasterService.error('Unable to retrieve quarterly leave information.');
            resolve({ isValid: false });
          }
        },
        error: () => {
          this.toasterService.error('Error validating quarterly limit. Please try again.');
          resolve({ isValid: false });
        }
      });
    });
  }

  private getQuarterFromMonth(month: number): number {
    if (month <= 3) return 1;
    if (month <= 6) return 2;
    if (month <= 9) return 3;
    return 4;
  }
  
  private getQuarterDataFromTimesheet(timesheetData: any[], selectedMonth: number, selectedYear: number): any {
    const monthData = timesheetData.find((data: any) => 
      data.monthNumber === selectedMonth.toString() && data.year === selectedYear
    );
    
    if (monthData && (monthData.quarterlyLimit !== undefined || monthData.quarterlyLeavesAvailed !== undefined)) {
      return monthData;
    }
    const quarter = this.getQuarterFromMonth(selectedMonth);
    for (const data of timesheetData) {
      if (data.year === selectedYear) {
        const dataMonth = parseInt(data.monthNumber);
        const dataQuarter = this.getQuarterFromMonth(dataMonth);
        
        if (dataQuarter === quarter && (data.quarterlyLimit !== undefined || data.quarterlyLeavesAvailed !== undefined)) {
          return data;
        }
      }
    }
    
    return null;
  }

  onCalendarMonthChanged(event: {month: number, year: number}) {
    this.refreshLeaveData(event.month, event.year);
  }

  private processLeaveSummary(): void {
    if (!this.timesheetData || this.timesheetData.length === 0) {
      this.processedLeaveSummary = [];
      return;
    }

    // Calculate totals from all timesheet entries
    const totals = this.timesheetData.reduce((acc, entry) => {
      return {
        paidLeaves: acc.paidLeaves + (entry.paidLeaves || 0),
        unpaidLeaves: acc.unpaidLeaves + (entry.unpaidLeaves || 0),
        totalLeaves: acc.totalLeaves + (entry.totalLeaves || 0),
        quarterlyLimit: Math.max(acc.quarterlyLimit, entry.quarterlyLimit || 0),
        quarterlyLeavesAvailed: Math.max(acc.quarterlyLeavesAvailed, entry.quarterlyLeavesAvailed || 0)
      };
    }, {
      paidLeaves: 0,
      unpaidLeaves: 0,
      totalLeaves: 0,
      quarterlyLimit: 0,
      quarterlyLeavesAvailed: 0
    });

    // Create summary items from timesheet data
    this.processedLeaveSummary = [
      { text: 'Total Leaves', value: totals.totalLeaves },
      { text: 'Paid Leaves', value: totals.paidLeaves },
      { text: 'Unpaid Leaves', value: totals.unpaidLeaves },
      { text: 'Quarterly Limit', value: totals.quarterlyLimit },
      { text: 'Quarterly Availed', value: totals.quarterlyLeavesAvailed }
    ] as SummaryItem[];
  }
  get filteredLeaveType() {
  const paidAllowed = this.quarterlyLimit !== null && this.quarterlyLimit > 0;
  return this.leaveType.filter(lt => {
    if (lt.value === 'Paid Leave' || lt.value === 'Half Paid Leave') {
      return paidAllowed;
    }
    return true;
  });
}
}